import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash, IconEdit, IconFileImport } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useBudget,
  useCreateBudgetLine,
  useUpdateBudgetLine,
  useDeleteBudgetLine,
  type BudgetLine,
} from "@/hooks/useBudget";
import { BudgetLineForm, type BudgetLineFormData } from "@/components/BudgetLineForm";
import { BudgetImportModal } from "@/components/BudgetImportModal";

export const Route = createFileRoute("/projects/$projectId/budget" as any)({
  component: ProjectBudgetPage,
});

function ProjectBudgetPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const { data, isLoading } = useBudget(projectId);
  const createBudgetLine = useCreateBudgetLine(projectId);
  const updateBudgetLine = useUpdateBudgetLine(projectId);
  const deleteBudgetLine = useDeleteBudgetLine(projectId);
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [importModalOpened, { open: openImportModal, close: closeImportModal }] = useDisclosure(false);
  const [editingLine, setEditingLine] = useState<BudgetLine | null>(null);
  const [lineToDelete, setLineToDelete] = useState<BudgetLine | null>(null);
  const [editingField, setEditingField] = useState<{
    lineId: string;
    field: "quantity" | "unit" | "unitCost";
    value: number | string;
  } | null>(null);

  const budgetLines = data?.budgetLines || [];
  const totals = data?.totals || {
    totalBudget: 0,
    totalLines: 0,
    divisionTotals: {},
  };

  const handleAddLine = async (formData: BudgetLineFormData) => {
    try {
      await createBudgetLine.mutateAsync({
        costCodeId: formData.costCodeId,
        description: formData.description,
        quantity: formData.quantity,
        unit: formData.unit,
        unitCost: formData.unitCost,
      });
      notifications.show({
        title: "Success",
        message: "Budget line added!",
        color: "green",
      });
      closeAddModal();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add budget line",
        color: "red",
      });
    }
  };

  const handleEditLine = async (formData: BudgetLineFormData) => {
    if (!editingLine) return;

    try {
      await updateBudgetLine.mutateAsync({
        id: editingLine.id,
        data: {
          costCodeId: formData.costCodeId,
          description: formData.description,
          quantity: formData.quantity,
          unit: formData.unit,
          unitCost: formData.unitCost,
        },
      });
      notifications.show({
        title: "Success",
        message: "Budget line updated!",
        color: "green",
      });
      closeEditModal();
      setEditingLine(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update budget line",
        color: "red",
      });
    }
  };

  const handleDeleteClick = (line: BudgetLine) => {
    setLineToDelete(line);
    openDeleteModal();
  };

  const handleDeleteConfirm = async () => {
    if (!lineToDelete) return;

    try {
      await deleteBudgetLine.mutateAsync(lineToDelete.id);
      notifications.show({
        title: "Success",
        message: "Budget line deleted!",
        color: "green",
      });
      closeDeleteModal();
      setLineToDelete(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete budget line",
        color: "red",
      });
    }
  };

  const openEdit = (line: BudgetLine) => {
    setEditingLine(line);
    openEditModal();
  };

  const handleInlineEdit = async (lineId: string, field: string, value: number | string) => {
    const line = budgetLines.find((l) => l.id === lineId);
    if (!line) return;

    try {
      await updateBudgetLine.mutateAsync({
        id: lineId,
        data: {
          [field]: value === "" ? undefined : Number(value),
        },
      });
      setEditingField(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update",
        color: "red",
      });
    }
  };

  // Group budget lines by division
  const groupedLines = budgetLines.reduce(
    (acc, line) => {
      const division = line.costCode.division;
      if (!acc[division]) {
        acc[division] = [];
      }
      acc[division].push(line);
      return acc;
    },
    {} as Record<string, BudgetLine[]>
  );

  // Sort divisions
  const sortedDivisions = Object.keys(groupedLines).sort((a, b) => {
    const aNum = parseInt(a.split(" ")[0] || "0");
    const bNum = parseInt(b.split(" ")[0] || "0");
    return aNum - bNum;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <Title order={2}>Budget</Title>
            <Group gap="sm">
              <Button
                variant="light"
                leftSection={<IconFileImport size={16} />}
                onClick={openImportModal}
              >
                Import CSV
              </Button>
              <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
                Add Budget Line
              </Button>
            </Group>
          </Group>

            {/* Budget Summary */}
            <Paper p="md" withBorder style={{ background: "var(--mantine-color-blue-0)" }}>
              <Group justify="space-between" align="center">
                <div>
                  <Text size="sm" c="dimmed">
                    Total Budget
                  </Text>
                  <Text size="xl" fw={700}>
                    {formatCurrency(totals.totalBudget)}
                  </Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed" ta="right">
                    Total Lines
                  </Text>
                  <Text size="lg" fw={600} ta="right">
                    {totals.totalLines}
                  </Text>
                </div>
              </Group>
            </Paper>

            {/* Division Breakdown */}
            {sortedDivisions.length > 0 && (
              <Paper p="md" withBorder>
                <Text size="sm" fw={500} mb="sm">
                  Division Breakdown
                </Text>
                <Group gap="md">
                  {sortedDivisions.map((division) => (
                    <Paper key={division} p="xs" withBorder>
                      <Text size="xs" c="dimmed">
                        {division}
                      </Text>
                      <Text size="sm" fw={600}>
                        {formatCurrency(totals.divisionTotals[division] || 0)}
                      </Text>
                    </Paper>
                  ))}
                </Group>
              </Paper>
            )}

            {budgetLines.length === 0 ? (
              <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
                <Stack gap="md" align="center">
                  <Title order={3} c="dimmed">No Budget Set Up</Title>
                  <Text c="dimmed" ta="center" maw={500}>
                    Import a budget to start tracking costs against your plan, or add budget lines manually to build your project budget from scratch.
                  </Text>
                  <Group gap="sm">
                    <Button
                      variant="light"
                      leftSection={<IconFileImport size={16} />}
                      onClick={openImportModal}
                    >
                      Import Budget CSV
                    </Button>
                    <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
                      Add Budget Line Manually
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed" ta="center" maw={600} mt="md">
                    Your budget is the foundation for cost tracking. Once set up, you can track commitments, actual costs, and monitor budget variance across all cost codes.
                  </Text>
                </Stack>
              </Paper>
            ) : (
              <Stack gap="xl">
                {sortedDivisions.map((division) => (
                  <div key={division}>
                    <Group justify="space-between" align="center" mb="sm">
                      <Badge variant="light" size="lg">
                        {division}
                      </Badge>
                      <Text size="sm" fw={500}>
                        Subtotal: {formatCurrency(totals.divisionTotals[division] || 0)}
                      </Text>
                    </Group>
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Cost Code</Table.Th>
                          <Table.Th>Description</Table.Th>
                          <Table.Th style={{ width: "100px", textAlign: "right" }}>Qty</Table.Th>
                          <Table.Th style={{ width: "80px" }}>Unit</Table.Th>
                          <Table.Th style={{ width: "120px", textAlign: "right" }}>
                            Unit Cost
                          </Table.Th>
                          <Table.Th style={{ width: "140px", textAlign: "right" }}>Total</Table.Th>
                          <Table.Th style={{ width: "100px" }}></Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {(groupedLines[division] || []).map((line) => (
                          <Table.Tr key={line.id}>
                            <Table.Td>
                              <Text size="sm" fw={500}>
                                {line.costCode.code}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{line.description}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              {editingField?.lineId === line.id &&
                              editingField.field === "quantity" ? (
                                <NumberInput
                                  size="xs"
                                  min={0}
                                  decimalScale={2}
                                  thousandSeparator=","
                                  defaultValue={line.quantity}
                                  onBlur={(e) => {
                                    const target = e.currentTarget as HTMLInputElement;
                                    handleInlineEdit(line.id, "quantity", target.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const target = e.currentTarget as HTMLInputElement;
                                      handleInlineEdit(line.id, "quantity", target.value);
                                    } else if (e.key === "Escape") {
                                      setEditingField(null);
                                    }
                                  }}
                                  autoFocus
                                  styles={{ input: { textAlign: "right" } }}
                                />
                              ) : (
                                <Text
                                  size="sm"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    setEditingField({
                                      lineId: line.id,
                                      field: "quantity",
                                      value: line.quantity,
                                    })
                                  }
                                >
                                  {formatNumber(line.quantity)}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {editingField?.lineId === line.id && editingField.field === "unit" ? (
                                <TextInput
                                  size="xs"
                                  defaultValue={line.unit}
                                  onBlur={(e) => {
                                    handleInlineEdit(line.id, "unit", e.currentTarget.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleInlineEdit(line.id, "unit", e.currentTarget.value);
                                    } else if (e.key === "Escape") {
                                      setEditingField(null);
                                    }
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <Text
                                  size="sm"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    setEditingField({
                                      lineId: line.id,
                                      field: "unit",
                                      value: line.unit,
                                    })
                                  }
                                >
                                  {line.unit}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              {editingField?.lineId === line.id &&
                              editingField.field === "unitCost" ? (
                                <NumberInput
                                  size="xs"
                                  prefix="$"
                                  min={0}
                                  decimalScale={2}
                                  thousandSeparator=","
                                  defaultValue={line.unitCost}
                                  onBlur={(e) => {
                                    const target = e.currentTarget as HTMLInputElement;
                                    handleInlineEdit(line.id, "unitCost", target.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const target = e.currentTarget as HTMLInputElement;
                                      handleInlineEdit(line.id, "unitCost", target.value);
                                    } else if (e.key === "Escape") {
                                      setEditingField(null);
                                    }
                                  }}
                                  autoFocus
                                  styles={{ input: { textAlign: "right" } }}
                                />
                              ) : (
                                <Text
                                  size="sm"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    setEditingField({
                                      lineId: line.id,
                                      field: "unitCost",
                                      value: line.unitCost,
                                    })
                                  }
                                >
                                  {formatCurrency(line.unitCost)}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              <Text size="sm" fw={600}>
                                {formatCurrency(line.total)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs" justify="flex-end">
                                <ActionIcon
                                  variant="subtle"
                                  color="blue"
                                  onClick={() => openEdit(line)}
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={() => handleDeleteClick(line)}
                                  loading={deleteBudgetLine.isPending}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>

                    {/* Add Line button at bottom of each division */}
                    <Group justify="flex-end" mt="xs">
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconPlus size={14} />}
                        onClick={openAddModal}
                      >
                        Add Line to {division}
                      </Button>
                    </Group>
                  </div>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>

      {/* Add Budget Line Modal */}
      <Modal opened={addModalOpened} onClose={closeAddModal} title="Add Budget Line" size="lg" centered>
        <BudgetLineForm
          onSubmit={handleAddLine}
          onCancel={closeAddModal}
          loading={createBudgetLine.isPending}
          submitText="Add Line"
          projectId={projectId}
        />
      </Modal>

      {/* Edit Budget Line Modal */}
      <Modal opened={editModalOpened} onClose={closeEditModal} title="Edit Budget Line" size="lg" centered>
        {editingLine && (
          <BudgetLineForm
            onSubmit={handleEditLine}
            onCancel={() => {
              closeEditModal();
              setEditingLine(null);
            }}
            initialData={editingLine}
            loading={updateBudgetLine.isPending}
            submitText="Save Changes"
            projectId={projectId}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title="Delete Budget Line"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this budget line? This action cannot be undone.
          </Text>
          {lineToDelete && (
            <Paper p="sm" withBorder>
              <Text size="sm" fw={500}>
                {lineToDelete.costCode.code} - {lineToDelete.description}
              </Text>
              <Text size="sm" c="dimmed">
                Total: {formatCurrency(lineToDelete.total)}
              </Text>
            </Paper>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeleteModal} disabled={deleteBudgetLine.isPending}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteConfirm} loading={deleteBudgetLine.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Import Budget Modal */}
      <BudgetImportModal
        opened={importModalOpened}
        onClose={closeImportModal}
        projectId={projectId}
      />
    </>
  );
}
